/**
 * Unified Time Slider Component
 *
 * A single slider that controls time navigation both backward (historical data)
 * and forward (concentration predictions/physics simulation).
 *
 * Features:
 * - Loads historical sampling events from database
 * - Shows "TODAY" marker at center
 * - Past dates show actual plume from well sampling data
 * - Future dates show predicted plume from physics/concentration model
 * - Play/pause animation through time
 *
 * TODO: Wire future plume prediction to XGBoost concentration model.
 * The XGBoost model predicts future concentrations at wells, which can
 * feed the Domenico engine for forward projection. See src/ml/concentration/
 */

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  PlayIcon,
  PauseIcon,
  BackwardIcon,
  ForwardIcon,
  ChevronDoubleLeftIcon,
  ChevronDoubleRightIcon,
} from '@heroicons/react/24/solid';
import { apiClient } from '../../api/client';
import {
  TimelineResponse,
  HistoricalPlumeStateResponse,
  HistoricalCompoundBoundary,
  ContaminantBoundaryData,
  PlumeSnapshotsResponse,
  PredictiveSnapshotsResponse,
  QuarterlyPlumeSnapshot,
  fetchPlumeSnapshots,
  fetchPredictiveSnapshots,
} from '../../api/polygon';

// v3.2 calendar horizons that replace the legacy Q1..Q40 future-event
// placeholder. Each entry carries a `days` value that maps to a v3.2 prediction
// horizon — the parent uses it to swap per-well predicted concentrations onto
// the 2D and 3D maps.
const V32_HORIZONS: { days: number; label: string }[] = [
  { days: 90, label: '+3mo' },
  { days: 365, label: '+1yr' },
  { days: 730, label: '+2yr' },
  { days: 1825, label: '+5yr' },
  { days: 3650, label: '+10yr' },
  { days: 7300, label: '+20yr' },
];

export interface UnifiedTimeSliderProps {
  /** Site ID to load timeline for */
  siteId: string;
  /** Callback when plume boundaries change (historical or predicted) */
  onBoundariesChange: (boundaries: ContaminantBoundaryData[]) => void;
  /** Callback when flow direction changes */
  onFlowDirectionChange?: (direction: number) => void;
  /** Callback when date changes */
  onDateChange?: (date: string, isHistorical: boolean) => void;
  /**
   * Fires whenever the slider's current event is a v3.2 horizon (future side).
   * Receives `days` (90 / 365 / 730 / 1825 / 3650 / 7300) for the chosen
   * horizon, or `0` when the slider is at "today" / a historical date — the
   * parent should drop any v3.2 well-prediction overrides in that case.
   */
  onHorizonChange?: (days: number) => void;
  /** Current/latest plume boundaries (used as "today" state) */
  currentBoundaries?: ContaminantBoundaryData[];
  /** Current flow direction */
  currentFlowDirection?: number;
  /** Whether to auto-load timeline on mount */
  autoLoad?: boolean;
  /** Compounds to filter (optional) */
  compounds?: string[];
  /** Custom class name */
  className?: string;
  /** Callback when PSH wells change (from snapshot or null for API fallback) */
  onPshWellsChange?: (wells: Array<{ well_id: string; lat: number; lon: number; product_thickness_ft: number; depth_to_water_ft?: number; is_inferred?: boolean }> | null) => void;
  /** Pre-fetched timeline from page-bundle (skips API call if provided) */
  bundledTimeline?: TimelineResponse | null;
  /** Pre-fetched plume snapshots from page-bundle (skips API call if provided) */
  bundledSnapshots?: PlumeSnapshotsResponse | null;
  /** Pre-fetched predictive snapshots from page-bundle (skips API call if provided) */
  bundledPredictiveSnapshots?: PredictiveSnapshotsResponse | null;
}

interface TimelineEvent {
  date: string;
  type: 'historical' | 'today' | 'prediction';
  label: string;
  hasData: boolean;
  /** v3.2 horizon-in-days when this event represents a v3.2 prediction node
   *  (90 / 365 / 730 / 1825 / 3650 / 7300). Drives the parent's per-well
   *  prediction overrides on the 2D and 3D maps. Undefined for historical
   *  events, "today", and per-date backend predictions. */
  horizonDays?: number;
}

export function UnifiedTimeSlider({
  siteId,
  onBoundariesChange,
  onFlowDirectionChange,
  onDateChange,
  onHorizonChange,
  currentBoundaries = [],
  currentFlowDirection = 180,
  autoLoad = true,
  compounds,
  className = '',
  onPshWellsChange,
  bundledTimeline,
  bundledSnapshots,
  bundledPredictiveSnapshots,
}: UnifiedTimeSliderProps) {
  // Timeline data
  const [timeline, setTimeline] = useState<TimelineResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Cached historical states (to avoid re-fetching)
  const [historicalCache, setHistoricalCache] = useState<Map<string, HistoricalPlumeStateResponse>>(new Map());

  // Pre-computed quarterly snapshots (loaded in bulk on mount)
  const [snapshots, setSnapshots] = useState<PlumeSnapshotsResponse | null>(null);

  // Pre-computed PREDICTIVE (future) snapshots — separate package from historical
  const [predictiveSnapshots, setPredictiveSnapshots] = useState<PredictiveSnapshotsResponse | null>(null);

  // O(1) lookup map: date string → snapshot (built once from snapshots)
  const snapshotMap = useMemo(() => {
    if (!snapshots?.precomputed) return null;
    const map = new Map<string, QuarterlyPlumeSnapshot>();
    for (const s of snapshots.snapshots) map.set(s.date, s);
    return map;
  }, [snapshots]);

  // O(1) lookup map for predictive (future) snapshots
  const predictiveMap = useMemo(() => {
    if (!predictiveSnapshots?.precomputed) return null;
    const map = new Map<string, QuarterlyPlumeSnapshot>();
    for (const s of predictiveSnapshots.snapshots) map.set(s.date, s);
    return map;
  }, [predictiveSnapshots]);

  // Debounce ref for fallback per-date API calls
  const debouncedLoadRef = useRef<ReturnType<typeof setTimeout>>();

  // Slider state - start at -1 (uninitialized) to prevent premature data loading
  const [currentIndex, setCurrentIndex] = useState(-1);
  const [isInitialized, setIsInitialized] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [playSpeed, setPlaySpeed] = useState(1000); // ms per frame
  const [playDirection, setPlayDirection] = useState<'forward' | 'backward'>('forward');


  // Build unified timeline events from backend data
  // When pre-computed snapshots are available, use quarterly ticks for historical dates.
  // Otherwise fall back to per-date events from the timeline endpoint.
  const timelineEvents = useMemo((): TimelineEvent[] => {
    const events: TimelineEvent[] = [];
    const today = timeline?.today || new Date().toISOString().split('T')[0];
    const todayDate = new Date(today);

    // === SNAPSHOT MODE: Use quarterly ticks for historical dates ===
    if (snapshots?.precomputed && snapshots.snapshots.length > 0) {
      // Add quarterly snapshot events (historical)
      for (const snap of snapshots.snapshots) {
        if (snap.date <= today) {
          events.push({
            date: snap.date,
            type: 'historical',
            label: snap.quarter,
            hasData: true,
          });
        }
      }

      // Add "today" marker
      events.push({
        date: today,
        type: 'today',
        label: 'Today',
        hasData: true,
      });

      // FUTURE SIDE — exactly the 6 v3.2 horizons. Backend per-date prediction
      // events and predictive snapshots are deliberately ignored: those nodes
      // had no `horizonDays`, so landing on one would fall through to a per-
      // date API call that doesn't have data for future dates (slider hangs
      // on "Loading..."). Each v3.2 tick carries `horizonDays` and triggers
      // the parent's `applyHorizonOverridesToBoundaries` short-circuit.
      for (const h of V32_HORIZONS) {
        const futureDate = new Date(todayDate);
        futureDate.setDate(futureDate.getDate() + h.days);
        events.push({
          date: futureDate.toISOString().split('T')[0],
          type: 'prediction',
          label: h.label,
          hasData: true,
          horizonDays: h.days,
        });
      }

      events.sort((a, b) => a.date.localeCompare(b.date));
      return events;
    }

    // === FALLBACK MODE: Per-date events from timeline endpoint ===
    // Skip backend prediction events — the future side of the slider is
    // owned entirely by the 6 v3.2 horizons added below.
    if (timeline?.events) {
      for (const event of timeline.events) {
        const eventDate = event.date;
        const isToday = eventDate === today;
        const isPrediction = event.is_prediction || event.type === 'prediction';

        if (isToday) {
          events.push({
            date: eventDate,
            type: 'today',
            label: 'Today',
            hasData: true,
          });
        } else if (isPrediction) {
          // Skip — the v3.2 horizons below are the only future-side ticks.
          continue;
        } else {
          events.push({
            date: eventDate,
            type: 'historical',
            label: formatDate(eventDate),
            hasData: event.has_sampling,
          });
        }
      }
    }

    // Always ensure "today" exists in the timeline
    const hasTodayEvent = events.some(e => e.type === 'today');
    if (!hasTodayEvent) {
      events.push({
        date: today,
        type: 'today',
        label: 'Today',
        hasData: true,
      });
    }

    // v3.2 calendar-horizon future nodes — the only future-side ticks the
    // slider exposes. Backend prediction events are deliberately filtered
    // out above so the slider can't land on a future date without a
    // `horizonDays` value (which would hang on a per-date API call).
    for (const h of V32_HORIZONS) {
      const futureDate = new Date(todayDate);
      futureDate.setDate(futureDate.getDate() + h.days);
      events.push({
        date: futureDate.toISOString().split('T')[0],
        type: 'prediction',
        label: h.label,
        hasData: true,
        horizonDays: h.days,
      });
    }

    // Sort by date
    events.sort((a, b) => a.date.localeCompare(b.date));

    return events;
  }, [timeline, snapshots, predictiveSnapshots]);

  // Find "today" index
  const todayIndex = useMemo(() => {
    return timelineEvents.findIndex(e => e.type === 'today');
  }, [timelineEvents]);

  // Current event
  const currentEvent = timelineEvents[currentIndex] || null;

  // Load timeline on mount or when siteId changes
  // When bundled data is provided (from page-bundle), use it directly — zero network calls.
  useEffect(() => {
    if (!autoLoad || !siteId) return;

    // Reset state when siteId changes to ensure fresh initialization
    setIsInitialized(false);
    setCurrentIndex(-1);
    setHistoricalCache(new Map());
    setSnapshots(null);

    // If bundled data was provided from page-bundle, use it directly (no API calls)
    if (bundledSnapshots !== undefined) {
      if (bundledSnapshots) setSnapshots(bundledSnapshots);
    } else {
      // Load pre-computed quarterly snapshots in parallel (non-blocking)
      fetchPlumeSnapshots(siteId)
        .then(setSnapshots)
        .catch((err) => console.warn('[UnifiedTimeSlider] Snapshots not available, using per-date fallback:', err));
    }

    // Load predictive (future) snapshots — separate package
    if (bundledPredictiveSnapshots !== undefined) {
      if (bundledPredictiveSnapshots) setPredictiveSnapshots(bundledPredictiveSnapshots);
    } else {
      fetchPredictiveSnapshots(siteId)
        .then(setPredictiveSnapshots)
        .catch((err) => console.warn('[UnifiedTimeSlider] Predictive snapshots not available:', err));
    }

    if (bundledTimeline !== undefined) {
      if (bundledTimeline) setTimeline(bundledTimeline);
      return; // Skip API call — bundled data is authoritative
    }

    const loadTimeline = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const resp = await apiClient.get<TimelineResponse>(
          `/polygon/physics/sites/${siteId}/timeline`
        );
        setTimeline(resp.data);
      } catch (err) {
        console.error('[UnifiedTimeSlider] Failed to load timeline:', err);
        setError(err instanceof Error ? err.message : 'Failed to load timeline');
      } finally {
        setIsLoading(false);
      }
    };

    loadTimeline();
  }, [siteId, autoLoad, bundledTimeline, bundledSnapshots, bundledPredictiveSnapshots]);

  // Set initial position to "today" when timeline loads
  // This ensures we always start at current data, not historical
  // IMPORTANT: Only initialize AFTER timeline has loaded (timeline !== null)
  // Otherwise todayIndex would be 0 before historical events are added
  useEffect(() => {
    if (timeline && todayIndex >= 0 && !isInitialized) {
      setCurrentIndex(todayIndex);
      setIsInitialized(true);
      // Ensure current boundaries are shown on init
      onBoundariesChange(currentBoundaries);
      onFlowDirectionChange?.(currentFlowDirection);
      onDateChange?.(new Date().toISOString().split('T')[0], false);
    }
  }, [timeline, todayIndex, timelineEvents.length, isInitialized, currentBoundaries, currentFlowDirection, onBoundariesChange, onFlowDirectionChange, onDateChange]);

  // Emit v3.2 horizon callback whenever the slider lands on a future node.
  // Fires `0` for historical / today so the parent drops any per-well
  // overrides and the maps revert to measured data.
  useEffect(() => {
    if (!isInitialized || !currentEvent) return;
    onHorizonChange?.(currentEvent.horizonDays ?? 0);
  }, [isInitialized, currentEvent, onHorizonChange]);

  // Load plume state when index changes (only after initialization)
  // Priority: snapshotMap (O(1) local) → historicalCache → per-date API call (debounced)
  useEffect(() => {
    // Don't load anything until we're properly initialized at "today"
    if (!isInitialized || currentIndex < 0 || !currentEvent || !siteId) return;

    // If it's "today", use current boundaries (already fetched)
    if (currentEvent.type === 'today') {
      onBoundariesChange(currentBoundaries);
      onFlowDirectionChange?.(currentFlowDirection);
      onDateChange?.(currentEvent.date, false);
      onPshWellsChange?.(null);  // No snapshot — fall back to API
      return;
    }

    // v3.2 horizon nodes — the backend has no `historical-state` row for
    // future dates so we'd hang the slider loading forever otherwise. Hand
    // off the current measured boundaries as the substrate; the parent's
    // `applyHorizonOverridesToBoundaries` will rewrite well concentrations
    // with the v3.2 prediction at this horizon and re-hull each compound's
    // polygon. PSH falls back to its standard API path.
    if (currentEvent.horizonDays != null) {
      onBoundariesChange(currentBoundaries);
      onFlowDirectionChange?.(currentFlowDirection);
      onDateChange?.(currentEvent.date, false);
      onPshWellsChange?.(null);
      return;
    }

    // Priority 1a: Pre-computed HISTORICAL snapshot (O(1) local Map lookup — instant)
    if (snapshotMap) {
      const snapshot = snapshotMap.get(currentEvent.date);
      if (snapshot) {
        const boundaries = convertToContaminantBoundaries(snapshot.compound_boundaries);
        onBoundariesChange(boundaries);
        onFlowDirectionChange?.(snapshot.flow_direction_deg);
        onDateChange?.(snapshot.date, true);
        onPshWellsChange?.(snapshot.psh_wells || null);
        return;
      }
    }

    // Priority 1b: Pre-computed PREDICTIVE snapshot (future dates — separate package)
    if (predictiveMap) {
      const predictive = predictiveMap.get(currentEvent.date);
      if (predictive) {
        const boundaries = convertToContaminantBoundaries(predictive.compound_boundaries);
        onBoundariesChange(boundaries);
        onFlowDirectionChange?.(predictive.flow_direction_deg);
        onDateChange?.(predictive.date, false);  // isHistorical=false for predictions
        onPshWellsChange?.(predictive.psh_wells || null);
        return;
      }
    }

    // Priority 2: In-memory cache from previous fetches
    const cached = historicalCache.get(currentEvent.date);
    if (cached) {
      const boundaries = convertToContaminantBoundaries(cached.compound_boundaries);
      onBoundariesChange(boundaries);
      onFlowDirectionChange?.(cached.flow_direction_deg);
      const isHistorical = cached.data_source !== 'prediction';
      onDateChange?.(currentEvent.date, isHistorical);
      onPshWellsChange?.(null);  // Cache hit but no snapshot — fall back to API
      return;
    }

    // Priority 3: Per-date API call (debounced 150ms to avoid flooding during rapid scrub)
    onPshWellsChange?.(null);  // No snapshot — fall back to API for PSH
    clearTimeout(debouncedLoadRef.current);
    debouncedLoadRef.current = setTimeout(async () => {
      setIsLoading(true);
      try {
        const params: Record<string, string> = {};
        if (compounds?.length) params.compounds = compounds.join(',');
        const resp = await apiClient.get<HistoricalPlumeStateResponse>(
          `/polygon/physics/sites/${siteId}/historical-state/${currentEvent.date}`,
          { params }
        );
        const state = resp.data;

        // Cache it
        setHistoricalCache(prev => new Map(prev).set(currentEvent.date, state));

        // Convert and emit
        const boundaries = convertToContaminantBoundaries(state.compound_boundaries);
        onBoundariesChange(boundaries);
        onFlowDirectionChange?.(state.flow_direction_deg);
        const isHistorical = state.data_source !== 'prediction';
        onDateChange?.(currentEvent.date, isHistorical);
      } catch (err) {
        console.error('[UnifiedTimeSlider] Failed to load plume state:', err);
        if (currentEvent.type === 'prediction' && !currentEvent.hasData) {
          onBoundariesChange([]);
          onDateChange?.(currentEvent.date, false);
        } else {
          onBoundariesChange(currentBoundaries);
        }
      } finally {
        setIsLoading(false);
      }
    }, 150);
  }, [isInitialized, currentIndex, currentEvent, siteId, compounds, currentBoundaries, currentFlowDirection, historicalCache, snapshotMap, predictiveMap, onBoundariesChange, onFlowDirectionChange, onDateChange, onPshWellsChange]);

  // Auto-play animation
  useEffect(() => {
    if (!isPlaying) return;

    const interval = setInterval(() => {
      setCurrentIndex(prev => {
        if (playDirection === 'forward') {
          const next = prev + 1;
          if (next >= timelineEvents.length) {
            setIsPlaying(false);
            return prev;
          }
          return next;
        } else {
          const next = prev - 1;
          if (next < 0) {
            setIsPlaying(false);
            return prev;
          }
          return next;
        }
      });
    }, playSpeed);

    return () => clearInterval(interval);
  }, [isPlaying, playDirection, playSpeed, timelineEvents.length]);

  // Handlers
  const handleSliderChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    // Auto-pause when user manually changes slider
    if (isPlaying) {
      setIsPlaying(false);
    }
    setCurrentIndex(parseInt(e.target.value));
  }, [isPlaying]);

  // Handle click on track to jump to that position
  // Track is scaled with today at 50%, so we need to convert back
  const handleTrackClick = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    if (!isInitialized || isLoading || todayIndex < 0) return;

    const max = timelineEvents.length - 1;
    if (max <= 0) return;

    const track = e.currentTarget;
    const rect = track.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const percent = (clickX / rect.width) * 100;

    // Convert visual percent back to index (today at 50%)
    let newIndex: number;
    if (percent <= 50) {
      // Left side: 0-50% maps to 0-todayIndex
      newIndex = Math.round((percent / 50) * todayIndex);
    } else {
      // Right side: 50-100% maps to todayIndex-max
      const futureRange = max - todayIndex;
      newIndex = todayIndex + Math.round(((percent - 50) / 50) * futureRange);
    }

    // Auto-pause and jump to position
    if (isPlaying) {
      setIsPlaying(false);
    }
    setCurrentIndex(Math.max(0, Math.min(max, newIndex)));
  }, [isInitialized, isLoading, isPlaying, timelineEvents.length, todayIndex]);

  const handlePlayPause = useCallback(() => {
    if (isPlaying) {
      setIsPlaying(false);
    } else {
      // If at end, restart from beginning
      if (playDirection === 'forward' && currentIndex >= timelineEvents.length - 1) {
        setCurrentIndex(0);
      } else if (playDirection === 'backward' && currentIndex <= 0) {
        setCurrentIndex(timelineEvents.length - 1);
      }
      setIsPlaying(true);
    }
  }, [isPlaying, playDirection, currentIndex, timelineEvents.length]);

  const handlePrevious = useCallback(() => {
    if (isPlaying) setIsPlaying(false);
    setCurrentIndex(prev => Math.max(0, prev - 1));
  }, [isPlaying]);

  const handleNext = useCallback(() => {
    if (isPlaying) setIsPlaying(false);
    setCurrentIndex(prev => Math.min(timelineEvents.length - 1, prev + 1));
  }, [isPlaying, timelineEvents.length]);

  const handleFirst = useCallback(() => {
    if (isPlaying) setIsPlaying(false);
    setCurrentIndex(0);
  }, [isPlaying]);

  const handleLast = useCallback(() => {
    if (isPlaying) setIsPlaying(false);
    setCurrentIndex(timelineEvents.length - 1);
  }, [isPlaying, timelineEvents.length]);

  const handleGoToToday = useCallback(() => {
    if (isPlaying) setIsPlaying(false);
    if (todayIndex >= 0) {
      setCurrentIndex(todayIndex);
    }
  }, [isPlaying, todayIndex]);

  // Render
  if (error) {
    return (
      <div className={`bg-red-900/20 border border-red-800 rounded-lg p-3 ${className}`}>
        <p className="text-red-400 text-sm">{error}</p>
      </div>
    );
  }

  const maxIndex = timelineEvents.length - 1;
  // Handle uninitialized state (currentIndex = -1) by using todayIndex for display
  const displayIndex = currentIndex >= 0 ? currentIndex : (todayIndex >= 0 ? todayIndex : 0);

  // CRITICAL: Today should ALWAYS be at 50% (center of slider)
  // Scale past events to left 50%, future events to right 50%
  const todayPercent = 50; // Always center

  // Calculate progress percent with today fixed at center
  const progressPercent = useMemo(() => {
    if (todayIndex < 0 || maxIndex <= 0) return 50;

    if (displayIndex === todayIndex) {
      return 50; // At today = center
    } else if (displayIndex < todayIndex) {
      // In the past: scale 0 to todayIndex into 0% to 50%
      const pastProgress = displayIndex / todayIndex;
      return pastProgress * 50;
    } else {
      // In the future: scale todayIndex to maxIndex into 50% to 100%
      const futureRange = maxIndex - todayIndex;
      if (futureRange <= 0) return 50;
      const futureProgress = (displayIndex - todayIndex) / futureRange;
      return 50 + (futureProgress * 50);
    }
  }, [displayIndex, todayIndex, maxIndex]);

  return (
    <div className={`bg-mono-900 border border-mono-800 rounded-lg px-4 py-3 ${className}`}>
      {/* Compact header row: controls + date + nav */}
      <div className="flex items-center gap-3">
        {/* Left nav */}
        <div className="flex items-center gap-0.5">
          <button
            onClick={handleFirst}
            disabled={displayIndex === 0 || isLoading || !isInitialized}
            className="p-1 rounded text-mono-500 hover:text-mono-200 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            title="Go to earliest"
          >
            <ChevronDoubleLeftIcon className="w-3.5 h-3.5" />
          </button>
          <button
            onClick={handlePrevious}
            disabled={displayIndex === 0 || isLoading || !isInitialized}
            className="p-1 rounded text-mono-500 hover:text-mono-200 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            title="Previous event"
          >
            <BackwardIcon className="w-3.5 h-3.5" />
          </button>
        </div>

        {/* Play/Pause + direction */}
        <div className="flex items-center gap-1">
          <button
            onClick={() => setPlayDirection('backward')}
            disabled={!isInitialized}
            className={`px-1.5 py-0.5 rounded text-[10px] font-medium transition-colors ${
              playDirection === 'backward'
                ? 'bg-mono-700 text-mono-200'
                : 'text-mono-500 hover:text-mono-300'
            } disabled:opacity-30`}
          >
            Past
          </button>
          <button
            onClick={handlePlayPause}
            disabled={!isInitialized}
            className={`p-1.5 rounded-full transition-colors ${
              isPlaying
                ? 'bg-mono-500 text-mono-950'
                : 'bg-mono-800 text-mono-400 hover:bg-mono-700'
            } disabled:opacity-30`}
          >
            {isPlaying ? (
              <PauseIcon className="w-3.5 h-3.5" />
            ) : (
              <PlayIcon className="w-3.5 h-3.5" />
            )}
          </button>
          <button
            onClick={() => setPlayDirection('forward')}
            disabled={!isInitialized}
            className={`px-1.5 py-0.5 rounded text-[10px] font-medium transition-colors ${
              playDirection === 'forward'
                ? 'bg-mono-700 text-mono-200'
                : 'text-mono-500 hover:text-mono-300'
            } disabled:opacity-30`}
          >
            Future
          </button>
        </div>

        {/* Slider track (fills remaining space) */}
        <div className="flex-1 relative pt-1 pb-1">
          {/* Tick marks */}
          <div className="absolute top-0 left-0 right-0 h-1 pointer-events-none">
            {maxIndex > 0 && timelineEvents.map((event, idx) => {
              let percent: number;
              if (idx === todayIndex) {
                percent = 50;
              } else if (idx < todayIndex) {
                percent = todayIndex > 0 ? (idx / todayIndex) * 50 : 0;
              } else {
                const futureRange = maxIndex - todayIndex;
                percent = futureRange > 0 ? 50 + ((idx - todayIndex) / futureRange) * 50 : 50;
              }
              if (event.type === 'today') return null;
              if (!event.hasData) return null;
              return (
                <div
                  key={event.date}
                  className="absolute w-px h-1 bg-mono-600"
                  style={{ left: `${percent}%` }}
                  title={event.label}
                />
              );
            })}
          </div>

          {/* Track */}
          <div
            className="relative h-1.5 cursor-pointer"
            onClick={handleTrackClick}
          >
            <div className="absolute inset-0 bg-mono-800 rounded-full overflow-hidden">
              {/* Progress fill */}
              <div
                className="absolute left-0 top-0 h-full bg-mono-500 transition-all duration-150 rounded-full"
                style={{ width: `${progressPercent}%` }}
              />
            </div>

            {/* Hidden range input */}
            <input
              type="range"
              min="0"
              max={maxIndex}
              value={displayIndex}
              onChange={handleSliderChange}
              disabled={isLoading || !isInitialized}
              className="absolute inset-0 w-full h-full opacity-0 cursor-pointer disabled:cursor-wait"
            />
          </div>

          {/* Today marker */}
          <div
            className="absolute top-1 w-px h-2.5 bg-mono-400 pointer-events-none z-10"
            style={{ left: `${todayPercent}%` }}
          />

          {/* Thumb */}
          <div
            className={`absolute top-1 -translate-y-1/2 w-3 h-3 rounded-full border shadow-sm transition-all pointer-events-none z-20 ${
              isLoading
                ? 'bg-mono-700 border-mono-600'
                : 'bg-mono-300 border-mono-400'
            }`}
            style={{ left: `calc(${progressPercent}% - 6px)` }}
          />

          {/* Track labels */}
          <div className="flex justify-between text-[9px] text-mono-600 mt-0.5">
            <span>{timelineEvents[0]?.label || ''}</span>
            <button
              onClick={handleGoToToday}
              className="text-mono-400 hover:text-mono-200 font-medium"
            >
              Today
            </button>
            <span>{timelineEvents[maxIndex]?.label || ''}</span>
          </div>
        </div>

        {/* Current date display */}
        <div className="text-right min-w-[80px]">
          <div className="text-sm font-semibold text-mono-200">
            {!isInitialized ? 'Today' : (currentEvent?.label || 'Today')}
          </div>
          {isInitialized && currentEvent && currentEvent.type !== 'today' && (
            <div className="text-[10px] text-mono-500">{currentEvent.date}</div>
          )}
          {isLoading && isInitialized && (
            <div className="text-[10px] text-mono-500 animate-pulse">Loading...</div>
          )}
        </div>

        {/* Right nav */}
        <div className="flex items-center gap-0.5">
          <button
            onClick={handleNext}
            disabled={displayIndex === maxIndex || isLoading || !isInitialized}
            className="p-1 rounded text-mono-500 hover:text-mono-200 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            title="Next event"
          >
            <ForwardIcon className="w-3.5 h-3.5" />
          </button>
          <button
            onClick={handleLast}
            disabled={displayIndex === maxIndex || isLoading || !isInitialized}
            className="p-1 rounded text-mono-500 hover:text-mono-200 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            title="Go to latest"
          >
            <ChevronDoubleRightIcon className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
    </div>
  );
}

// Helper functions

function formatDate(dateStr: string): string {
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function convertToContaminantBoundaries(
  historicalBoundaries: HistoricalCompoundBoundary[]
): ContaminantBoundaryData[] {
  return historicalBoundaries.map(hb => ({
    compound: hb.compound,
    boundary: hb.boundary,
    wells_with_detections: hb.wells_with_detections,
    wells_below_threshold: hb.wells_below_threshold,
    max_concentration_ug_l: hb.max_concentration_ug_l,
    mcl_ug_l: hb.mcl_ug_l,
    threshold_ug_l: hb.threshold_ug_l,
    well_count: hb.well_count,
  }));
}

export default UnifiedTimeSlider;
